/**
 * Booking forms store – GET booking-forms/ and booking-forms/booked/
 * once per session. Refetch after POST (submit form).
 */

import { create } from "zustand";
import apiClient from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";

export interface BookingFormItem {
  id: number;
  deal_id: number;
  artist_id: number;
  artist_type: string;
  artist_act: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  full_address: string;
  city_state: string;
  members_count: number;
  members_info: string;
  report_at: string;
  performance_duration_mins: number;
  tech_rider: string;
  tbl_needed: number;
  travel_boarding_lodging: string;
  event_type: string;
  venue: string;
  venue_city_state: string;
  bank_name: string;
  branch_name: string;
  ifsc_code: string;
  account_number: string;
  account_name: string;
  account_type: string;
  pan: string;
  gstin: string | null;
  fee: string;
  advance_percentage: string;
  advance_amount: string;
  balance_amount: string;
  payment_terms: string;
  rating: number;
  feedback: string;
  status: string;
  created_at: string;
  updated_at: string;
  tech_rider_value: string;
  gst_value: string | null;
  event_date: string;
  pin_code: string | null;
  venue_city: string;
  venue_state: string;
  artist_state: string;
  artist_city: string;
  google_map_link: string | null;
  event_start_at: string | null;
  taf: string | null;
  mode: string | null;
  is_otp_verified?: boolean;
  express_booking_id?: number | null;
  express_booking_code?: string | null;
  event_time?: string | null;
  event_duration_mins?: number | null;
  description: string | null;
}

type ExpressBookedItem = {
  id?: number;
  express_booking_id?: number;
  // Legacy shape (older API)
  pipedrive_deal_id?: number;
  booking_id?: string;
  artist_id?: number;
  artist_user_id?: number;
  selected_bank_id?: number;
  selected_bank_snapshot?: {
    id?: number;
    branch?: string;
    default?: boolean;
    bank_name?: string;
    ifsc_code?: string;
    account_type?: string;
    bank_address?: string;
    account_number?: string;
    account_holder_name?: string;
  };
  pan_snapshot?: {
    pan?: string;
    gstin?: string | null;
    registered_address?: string;
  };
  artist_snapshot?: {
    email?: string;
    phone?: string;
    professional_name?: string;
  };
  submission_status?: string;
  submitted_at?: string;

  // New shape (current API)
  selected_bank?: {
    id?: number;
    branch?: string;
    default?: boolean;
    bank_name?: string;
    ifsc_code?: string;
    account_type?: string;
    bank_address?: string;
    account_number?: string;
    account_holder_name?: string;
  };
  submitted_pan_details?: {
    pan?: string;
    gstin?: string | null;
    registered_address?: string;
  };
  is_otp_verified?: boolean;
  detail?: {
    pipedrive_deal_id?: number;
    express_booking_id?: number;
    booking_id?: string;
    artist_details?: {
      professional_name?: string;
      email?: string;
      phone?: string;
    };
    event_details?: {
      event_type?: string;
      event_date?: string;
      event_time?: string;
      event_duration?: number;
      venue_type?: string | null;
      gathering_size?: string;
    };
    location?: {
      address?: string;
      city?: string;
      state?: string;
      pin_code?: string;
      latitude?: string;
      longitude?: string;
      map_link?: string;
    };
    fee_and_payment?: {
      base_price?: string;
      taf_percent?: string;
      taf_amount?: string;
      final_balance?: string;
      payment_terms?: string;
      is_sound_setup_added?: boolean;
      sound_setup_price?: string;
    };
  };
};

type BookedResponseNewShape = {
  success?: boolean;
  data?: {
    artist_booking_forms?: BookingFormItem[];
    express_booking_forms?: ExpressBookedItem[];
  };
};

interface BookingFormsState {
  toFill: BookingFormItem[];
  submitted: BookingFormItem[];
  hasFetchedInSession: boolean;
  toFillLoading: boolean;
  submittedLoading: boolean;
  error: string | null;

  fetchBookingForms: () => Promise<void>;
  ensureFetched: () => Promise<void>;
  setError: (error: string | null) => void;
  reset: () => void;
}

let inFlight: Promise<void> | null = null;

const initialState = {
  toFill: [],
  submitted: [],
  hasFetchedInSession: false,
  toFillLoading: false,
  submittedLoading: false,
  error: null as string | null,
};

function toStringOrEmpty(v: unknown): string {
  if (typeof v === "string") return v;
  if (v == null) return "";
  return String(v);
}

function mapExpressBookedToBookingFormItem(item: ExpressBookedItem): BookingFormItem | null {
  const dealId = Number(item.detail?.pipedrive_deal_id ?? item.pipedrive_deal_id);
  if (!Number.isFinite(dealId)) return null;

  const bank = item.selected_bank ?? item.selected_bank_snapshot ?? {};
  const pan = item.submitted_pan_details ?? item.pan_snapshot ?? {};
  const artist =
    item.detail?.artist_details ??
    item.artist_snapshot ??
    {};

  const event = item.detail?.event_details ?? {};
  const location = item.detail?.location ?? {};
  const fee = item.detail?.fee_and_payment ?? {};

  const accountTypeRaw = toStringOrEmpty(bank.account_type).trim().toLowerCase();
  const account_type = accountTypeRaw === "current" ? "Current" : "Savings";

  const venue_city = toStringOrEmpty(location.city).trim();
  const venue_state = toStringOrEmpty(location.state).trim();
  const venue_city_state =
    venue_city && venue_state ? `${venue_city}, ${venue_state}` : venue_city || venue_state;

  return {
    id: dealId,
    deal_id: dealId,
    artist_id: Number(item.artist_id ?? 0) || 0,
    artist_type: "Express",
    artist_act: toStringOrEmpty(artist.professional_name).trim() || "—",
    contact_name: toStringOrEmpty(artist.professional_name).trim(),
    contact_phone: toStringOrEmpty(artist.phone).trim(),
    contact_email: toStringOrEmpty(artist.email).trim(),
    full_address: (pan.registered_address ?? "").trim(),
    city_state: "",
    members_count: 0,
    members_info: "[]",
    report_at: "",
    performance_duration_mins: 0,
    tech_rider: "0",
    tbl_needed: 0,
    travel_boarding_lodging: "",
    event_type: toStringOrEmpty(event.event_type).trim(),
    venue: toStringOrEmpty(location.address).trim(),
    venue_city_state,
    bank_name: (bank.bank_name ?? "").trim(),
    branch_name: (bank.branch ?? "").trim(),
    ifsc_code: (bank.ifsc_code ?? "").trim(),
    account_number: (bank.account_number ?? "").trim(),
    account_name: (bank.account_holder_name ?? "").trim(),
    account_type,
    pan: (pan.pan ?? "").trim(),
    gstin: typeof pan.gstin === "string" ? pan.gstin : null,
    fee: toStringOrEmpty(fee.base_price).trim(),
    advance_percentage: "",
    advance_amount: "",
    balance_amount: "",
    payment_terms: toStringOrEmpty(fee.payment_terms).trim(),
    rating: 0,
    feedback: "",
    status: (item.submission_status ?? "").trim(),
    created_at: "",
    updated_at: "",
    tech_rider_value: "",
    gst_value: null,
    event_date: toStringOrEmpty(event.event_date).trim(),
    pin_code: null,
    venue_city: "",
    venue_state: "",
    artist_state: "",
    artist_city: "",
    google_map_link: typeof location.map_link === "string" ? location.map_link : null,
    event_start_at: null,
    taf: null,
    mode: "express",
    is_otp_verified: item.is_otp_verified === true,
    express_booking_id: Number(item.detail?.express_booking_id ?? item.express_booking_id ?? 0) || null,
    express_booking_code: toStringOrEmpty(item.detail?.booking_id ?? item.booking_id).trim() || null,
    event_time: toStringOrEmpty(event.event_time).trim() || null,
    event_duration_mins:
      typeof event.event_duration === "number" && Number.isFinite(event.event_duration)
        ? Math.max(0, Math.floor(event.event_duration))
        : null,
    description: null,
  };
}

export const useBookingFormsStore = create<BookingFormsState>()((set, get) => ({
  ...initialState,

  fetchBookingForms: async () => {
    if (inFlight) return inFlight;
    inFlight = (async () => {
      set({ toFillLoading: true, submittedLoading: true, error: null });
      try {
        const [toFillRes, bookedRes] = await Promise.all([
          apiClient.get<{ success?: boolean; data?: BookingFormItem[] }>(
            API_ENDPOINTS.AUTH.ARTIST_BOOKING_FORMS
          ),
          apiClient.get<unknown>(
            API_ENDPOINTS.AUTH.ARTIST_BOOKING_FORMS_BOOKED
          ),
        ]);
        const toFill =
          toFillRes.data?.success && Array.isArray(toFillRes.data?.data)
            ? toFillRes.data.data
            : [];

        const bookedData = bookedRes.data as unknown;
        let submitted: BookingFormItem[] = [];

        // New shape: { success: true, data: { artist_booking_forms: [], express_booking_forms: [] } }
        const maybeNew = bookedData as BookedResponseNewShape;
        if (
          maybeNew &&
          typeof maybeNew === "object" &&
          typeof maybeNew.data === "object" &&
          maybeNew.data
        ) {
          const artistBooked = Array.isArray(maybeNew.data.artist_booking_forms)
            ? maybeNew.data.artist_booking_forms
            : [];
          const expressBookedRaw = Array.isArray(maybeNew.data.express_booking_forms)
            ? maybeNew.data.express_booking_forms
            : [];
          const expressBooked = expressBookedRaw
            .map(mapExpressBookedToBookingFormItem)
            .filter((x): x is BookingFormItem => Boolean(x));

          // Ensure Express submitted items show first in the UI list.
          submitted = [...expressBooked, ...artistBooked];
        } else if (Array.isArray(bookedData)) {
          // Back-compat: array
          submitted = bookedData as BookingFormItem[];
        } else if (bookedData && typeof bookedData === "object") {
          // Back-compat: { data: BookingFormItem[] }
          const d = (bookedData as { data?: unknown }).data;
          submitted = Array.isArray(d) ? (d as BookingFormItem[]) : [];
        }

        set({
          toFill,
          submitted,
          hasFetchedInSession: true,
          toFillLoading: false,
          submittedLoading: false,
          error: null,
        });
      } catch {
        set({
          toFill: [],
          submitted: [],
          toFillLoading: false,
          submittedLoading: false,
          error: "Failed to load booking forms",
        });
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  },

  ensureFetched: async () => {
    const { hasFetchedInSession, fetchBookingForms } = get();
    if (hasFetchedInSession) return;
    return fetchBookingForms();
  },

  setError: (error) => set({ error }),

  reset: () => {
    inFlight = null;
    set(initialState);
  },
}));
